import React, { useState, useEffect } from "react";
import { AppBar, Toolbar, Button, Dialog, IconButton, Drawer, List, ListItemButton, ListItemText } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation } from "react-router-dom";
import "./navBar.css";
import SignIn from "../../public/signin/signin.tsx";
import ForgotPassword from "../../public/forgotPassword/forgotPassword.tsx";
import VerifyOtp from "../../public/verifyOtp/verifyOtp.tsx";
import ResetPassword from "../../public/resetPassword/resetPassword.tsx";
import { useSelector } from "react-redux";
import LogoutButton from "../../private/logout/logout.tsx";
import { logoutUser } from "../../../services/authAPI.js";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "About Us", path: "/about" },
  { label: "Contact Us", path: "/contact" },
];

interface NavbarProps {
  onOpenLogin?: (openLogin: () => void) => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenLogin }) => {
  const authUser = useSelector((state: any) => state.auth.user);

  const [loginOpen, setLoginOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dialogView, setDialogView] = useState<"signin" | "forgot" | "otp" | "reset">("signin");
  const [resetEmail, setResetEmail] = useState("");
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleOpenLogin = () => {
    setLoginOpen(true);
    setDialogView("signin");
    setError(null);
    setSuccess(false);
  };

  const handleLogout = async () => {
    const refreshToken = localStorage.getItem("refreshToken");

    try {
      await logoutUser(refreshToken);
    } catch (e) {}

    localStorage.clear();
    window.location.href = "/";
  };

  const handleCloseLogin = () => {
    if (!loading) {
      setLoginOpen(false);
      setDialogView("signin");
      setResetEmail("");
      setCredentials({
        email: "",
        password: "",
      });
      setError(null);
      setSuccess(false);
    }
  };

  useEffect(() => {
    onOpenLogin?.(handleOpenLogin);
  }, []); // eslint-disable-line

  return (
    <>
      <AppBar position='static' className='navbar-root'>
        <Toolbar className='navbar-toolbar'>
          {/* Left Section */}
          <div className='navbar-left'>
            <div className='navbar-logo'>
              <div  onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                <img src='/uyirthunai.png' alt='UyirThunai Logo' />
              </div>
            </div>
          </div>
          <div className='mobile-menu-btn'>
            <IconButton onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          </div>
          {/* Desktop Navigation */}
          <nav className='navbar-center'>
            {NAV_LINKS.map((item) => (
              <a
                key={item.label}
                href={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.path);
                }}
                className={`nav-link${location.pathname === item.path ? " active" : ""}`}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Right Section */}
          <div className='navbar-right'>
            {!authUser ? (
              <>
                <span className='member-text'>Already a member?</span>
                <Button onClick={handleOpenLogin} variant='outlined' className='btn-login'>
                  Log In
                </Button>
              </>
            ) : (
              <div className='desktop-logout'>
                <LogoutButton />
              </div>
            )}
            <Button onClick={() => navigate("/register")} variant='contained' className='btn-register'>
              Create Free Profile
            </Button>
          </div>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor='left' open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <div className='mobile-drawer'>
          <List>
            {NAV_LINKS.map((item) => (
              <ListItemButton
                key={item.label}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}

            {!authUser ? (
              <ListItemButton
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleOpenLogin();
                }}
              >
                <ListItemText primary='Log In' />
              </ListItemButton>
            ) : (
              <ListItemButton
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
              >
                <ListItemText primary='Log Out' />
              </ListItemButton>
            )}
            <ListItemButton
              onClick={() => {
                navigate("/register");
                setMobileMenuOpen(false);
              }}
            >
              <ListItemText primary='Create Free Profile' />
            </ListItemButton>
          </List>
        </div>
      </Drawer>

      {/* Login Dialog */}
      <Dialog
        open={loginOpen}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;

          handleCloseLogin();
        }}
        maxWidth='md'
        fullWidth
      >
        <IconButton onClick={handleCloseLogin} disabled={loading} className='dialog-close-btn'>
          <CloseIcon className='dialog-close-icon' />
        </IconButton>

        {dialogView === "signin" && (
          <SignIn
            credentials={credentials}
            setCredentials={setCredentials}
            loading={loading}
            setLoading={setLoading}
            error={error}
            setError={setError}
            success={success}
            setSuccess={setSuccess}
            setLoginOpen={setLoginOpen}
            setDialogView={setDialogView}
          />
        )}

        {dialogView === "forgot" && <ForgotPassword setDialogView={setDialogView} setResetEmail={setResetEmail} />}

        {dialogView === "otp" && <VerifyOtp resetEmail={resetEmail} setDialogView={setDialogView} />}

        {dialogView === "reset" && <ResetPassword resetEmail={resetEmail} setDialogView={setDialogView} />}
      </Dialog>
    </>
  );
};

export default Navbar;
